import { createTheme } from '@mui/material/styles';

// Paleta de cores Barba Barão
export const colors = {
  blackMatte: '#1A1A1A',      // Preto fosco - primária
  beige: '#C9B18C',           // Bege / couro envelhecido - secundária
  darkWine: '#6E2E1E',        // Vinho escuro - destaque
  agedGold: '#C2A14C',        // Dourado envelhecido - detalhes/selo
  whiteDefault: '#FFFFFF',        // Branco padrão - fundo/texto
};

// Criar tema customizado MUI
const theme = createTheme({
  palette: {
    primary: {
      main: colors.blackMatte,
      contrastText: colors.beige,
    },
    secondary: {
      main: colors.beige,
      contrastText: colors.blackMatte,
    },
    error: {
      main: colors.darkWine,
    },
    warning: {
      main: colors.agedGold,
    },
    background: {
      default: colors.beige,
      paper: colors.blackMatte,
    },
    text: {
      primary: colors.blackMatte,
      secondary: colors.beige,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Raleway Dots", "Raleway", sans-serif',
      fontWeight: 400,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontFamily: '"Raleway", sans-serif',
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.3,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontFamily: '"Raleway", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.4,
      letterSpacing: '0.02em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontFamily: '"Raleway", sans-serif',
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontFamily: '"Raleway", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.5,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h6: {
      fontFamily: '"Raleway", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    body1: {
      fontFamily: '"Roboto", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Roboto", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Raleway", sans-serif',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '12px 32px',
          fontSize: '1rem',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
          position: 'relative',
          overflow: 'hidden',
          // Heurística #1: Feedback visual
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.4)',
          },
          // Heurística #1: Feedback de clique
          '&:active': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
          },
          // Heurística #7: Acessibilidade - foco visível
          '&:focus-visible': {
            outline: `3px solid ${colors.agedGold}`,
            outlineOffset: '2px',
          },
        },
        contained: {
          backgroundColor: colors.darkWine,
          color: colors.beige,
          border: `2px solid transparent`,
          // Heurística #4: Consistência em hover
          '&:hover': {
            backgroundColor: colors.agedGold,
            color: colors.blackMatte,
            borderColor: colors.agedGold,
          },
          '&:active': {
            backgroundColor: colors.agedGold,
          },
        },
        outlined: {
          borderColor: colors.agedGold,
          color: colors.beige,
          borderWidth: 2,
          // Heurística #2: Feedback de interação
          '&:hover': {
            borderWidth: 2,
            backgroundColor: colors.agedGold,
            color: colors.blackMatte,
            borderColor: colors.agedGold,
          },
          '&:active': {
            backgroundColor: `${colors.agedGold}dd`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
          // Heurística #2: Feedback hover
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
          // Heurística #1: Feedback visual
          '&:hover': {
            transform: 'scale(1.1)',
            backgroundColor: `${colors.agedGold}20`,
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
          // Heurística #7: Foco visível
          '&:focus-visible': {
            outline: `2px solid ${colors.agedGold}`,
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
          // Heurística #4: Feedback consistente
          '&:hover': {
            backgroundColor: `${colors.darkWine}cc`,
            paddingLeft: '32px',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1200px)': {
            maxWidth: '1400px',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;
